import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Stack,
  Text,
  TextField,
  PrimaryButton,
  DefaultButton,
  IconButton,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Panel,
  PanelType,
  Toggle,
  Dialog,
  DialogType,
  DialogFooter,
  MessageBar,
  MessageBarType,
  Separator,
  Spinner,
  Link,
  FontIcon,
  getTheme,
  PersonaCoin,
  PersonaSize,
  ActionButton
} from "@fluentui/react";
import SkillChooserFluent from "../components/SkillsPicker";
import { propTypes } from "react-bootstrap/esm/Image";
import { styles } from "./ProposalDashboard";

/**
 * People
 * - Lists people with pagination, search, and sorting
 * - Create / Update in a right-side Panel
 * - Delete with confirmation dialog
 * - Uses your Express routes at /api/people
 */
export default function People(props) {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 40, total: 0, totalPages: 1, sort: "name", order: "asc", search: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 350);

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState("create"); // 'create' | 'edit'
  const [form, setForm] = useState(emptyPerson());

  const [deleteTarget, setDeleteTarget] = useState(null); // {id, name}
  const [toast, setToast] = useState(null); // {type, text}

  const fetchPeople = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page: overrides.page ?? meta.page,
        limit: overrides.limit ?? meta.limit,
        sort: overrides.sort ?? meta.sort,
        order: overrides.order ?? meta.order,
        search: overrides.search ?? debouncedSearch,
        active: true
      };
      const res = await axios.get("/api/people", { params });
      setItems(res.data.data || []);
      setMeta(res.data.meta || { page: 1, limit: 40, total: 0, totalPages: 1, sort: "name", order: "desc", search: "" });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to load people");
    } finally {
      setLoading(false);
    }
  }, [meta.page, meta.limit, meta.sort, meta.order, debouncedSearch]);

  useEffect(() => { fetchPeople({ page: 1, search: debouncedSearch }); }, [debouncedSearch]);
  useEffect(() => { fetchPeople(); }, [meta.page, meta.limit, meta.sort, meta.order]);

  const columns = useMemo(() => {
    const onColumnClick = (ev, col) => {
      if (!col) return;
      const isSame = meta.sort === col.fieldName;
      const nextOrder = isSame && meta.order === "asc" ? "desc" : "asc";
      setMeta((m) => ({ ...m, sort: col.fieldName, order: nextOrder }));
    };
    return [
      { 
        key: "name", 
        name: "Name", 
        fieldName: "name", 
        minWidth: 160, 
        isResizable: true, 
        onColumnClick, 
        onRender: (item) => {
          return <Stack horizontal verticalAlign="center" tokens={{childrenGap: 8}}>
            <PersonaCoin size={PersonaSize.size24} imageUrl={item.image_url} text={item.name} />
            <Link href={`/people/${item.id}`}>{item.name}</Link>
          </Stack>
        }
      },
      { key: "title", name: "Title", fieldName: "title", minWidth: 120, isResizable: true, onColumnClick },
      { key: "designation", name: "Designation", fieldName: "designation", minWidth: 120, isResizable: true, onColumnClick },
      { 
        key: "active", 
        name: "Active", 
        fieldName: "active", 
        minWidth: 70, 
        onRender: (i) => (i.active ? <FontIcon iconName="CompletedSolid" style={{color: getTheme().palette.green}}/> : <FontIcon iconName="Blocked2Solid" style={{color: getTheme().palette.neutralSecondaryAlt}} />), 
        onColumnClick 
      }
    ];
  }, [meta.sort, meta.order]);

  function openCreate() {
    setPanelMode("create");
    setForm(emptyPerson());
    setPanelOpen(true);
  }
  function openEdit(row) {
    setPanelMode("edit");
    setForm({ ...emptyPerson(), ...row });
    setPanelOpen(true);
  }

  async function saveForm() {
    try {
      if (!form.name?.trim()) {
        setToast({ type: MessageBarType.error, text: "Name is required" });
        return;
      }
      if (panelMode === "create") {
        const res = await axios.post("/api/people", form);
        setToast({ type: MessageBarType.success, text: `Created ${res.data.name}` });
      } else {
        const res = await axios.patch(`/api/people/${form.id}`, form);
        setToast({ type: MessageBarType.success, text: `Updated ${res.data.name}` });
      }
      setPanelOpen(false);
      await fetchPeople();
    } catch (e) {
      setToast({ type: MessageBarType.error, text: e?.response?.data?.error || e.message || "Save failed" });
    }
  }

  async function confirmDelete() {
    try {
      await axios.delete(`/api/people/${deleteTarget.id}`);
      setToast({ type: MessageBarType.success, text: `Deleted ${deleteTarget.name}` });
      setDeleteTarget(null);
      await fetchPeople();
    } catch (e) {
      setToast({ type: MessageBarType.error, text: e?.response?.data?.error || e.message || "Delete failed" });
    }
  }

  return (
    <Stack tokens={{ childrenGap: 12 }} className={styles.root}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="end" tokens={{ childrenGap: 12 }}>
        <Stack tokens={{ childrenGap: 6 }}>
          <Text variant="xxLarge">People</Text>
          <Text variant="small">Manage team members used across proposals and CVs.</Text>
        </Stack>
        <ActionButton iconProps={{ iconName: "Add" }} text="New person" onClick={openCreate} />
      </Stack>

      <Stack className={styles.sectionCard} horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
        <TextField label="Search" value={search} onChange={(_, v) => setSearch(v || "")} placeholder="Search name, title, designation" styles={{ root: { width: 360 } }} />
        <TextField label="Page size" value={String(meta.limit)} onChange={(_, v) => setMeta((m) => ({ ...m, limit: clampInt(v, 1, 200) }))} styles={{ root: { width: 120 } }} />
        <DefaultButton onClick={() => fetchPeople({ page: 1, search })} text="Apply" />
      </Stack>

      {toast && (
        <MessageBar messageBarType={toast.type} isMultiline={false} onDismiss={() => setToast(null)}>
          {toast.text}
        </MessageBar>
      )}

      {error && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={false} onDismiss={() => setError("")}>{error}</MessageBar>
      )}

      <Stack className={styles.sectionCard}>
        {loading ? (
          <Stack horizontal horizontalAlign="center" styles={{ root: { padding: 40 } }}>
            <Spinner label="Loading people…" />
          </Stack>
        ) : (
          <DetailsList
            items={items}
            columns={columns}
            setKey="set"
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.none}
          />
        )}
      </Stack>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="small">Page {meta.page} of {meta.totalPages} · {meta.total} total</Text>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <ActionButton text="Prev" disabled={meta.page <= 1} onClick={() => setMeta((m) => ({ ...m, page: Math.max(1, m.page - 1) }))} />
          <ActionButton text="Next" disabled={meta.page >= meta.totalPages} onClick={() => setMeta((m) => ({ ...m, page: Math.min(m.totalPages, m.page + 1) }))} />
        </Stack>
      </Stack>

      {/* Create / Edit Panel */}
      <Panel
        isOpen={panelOpen}
        onDismiss={() => setPanelOpen(false)}
        type={PanelType.medium}
        headerText={panelMode === "create" ? "New person" : `Edit: ${form.name || "(no name)"}`}
        closeButtonAriaLabel="Close"
      >
        <Stack tokens={{ childrenGap: 12 }}>
          <RequiredTextField label="Name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
          <TextField label="Title" value={form.title || ""} onChange={(_, v) => setForm((f) => ({ ...f, title: v || null }))} />
          <TextField label="Designation" value={form.designation || ""} onChange={(_, v) => setForm((f) => ({ ...f, designation: v || null }))} />
          <SkillChooserFluent personId={form.id} />
          <TextField label="Default bio" multiline autoAdjustHeight value={form.default_bio || ""} onChange={(_, v) => setForm((f) => ({ ...f, default_bio: v || null }))} />
          <TextField label="Skills overview" multiline autoAdjustHeight value={form.skills_overview || ""} onChange={(_, v) => setForm((f) => ({ ...f, skills_overview: v || null }))} />
          <TextField label="Education" multiline autoAdjustHeight value={form.education || ""} onChange={(_, v) => setForm((f) => ({ ...f, education: v || null }))} />
          <TextField label="Affiliations" multiline autoAdjustHeight value={form.affiliations || ""} onChange={(_, v) => setForm((f) => ({ ...f, affiliations: v || null }))} />
          <TextField label="Awards" multiline autoAdjustHeight value={form.awards || ""} onChange={(_, v) => setForm((f) => ({ ...f, awards: v || null }))} />
          <TextField label="Employment history" multiline autoAdjustHeight value={form.employment_history || ""} onChange={(_, v) => setForm((f) => ({ ...f, employment_history: v || null }))} />
          <Toggle label="Active" checked={!!form.active} onChange={(_, checked) => setForm((f) => ({ ...f, active: !!checked }))} />

          <Separator />
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton text={panelMode === "create" ? "Create" : "Save changes"} onClick={saveForm} />
            <DefaultButton text="Cancel" onClick={() => setPanelOpen(false)} />
          </Stack>
        </Stack>
      </Panel>

      {/* Delete confirm */}
      <Dialog
        hidden={!deleteTarget}
        onDismiss={() => setDeleteTarget(null)}
        dialogContentProps={{ type: DialogType.largeHeader, title: "Delete person", subText: deleteTarget ? `Delete ${deleteTarget.name}? This cannot be undone.` : "" }}
      >
        <DialogFooter>
          <PrimaryButton onClick={confirmDelete} text="Delete" />
          <DefaultButton onClick={() => setDeleteTarget(null)} text="Cancel" />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
}

function emptyPerson() {
  return {
    id: undefined,
    name: "",
    title: null,
    designation: null,
    default_bio: null,
    skills_overview: null,
    education: null,
    affiliations: null,
    awards: null,
    employment_history: null,
    active: true,
    canonical_bio: null,
  };
}

function clampInt(v, min, max) {
  const n = parseInt(String(v || "").replace(/[^0-9]/g, ""), 10);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function useDebouncedValue(value, delayMs) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

function RequiredTextField({ label, value, onChange }) {
  const [touched, setTouched] = useState(false);
  const errorMessage = touched && !String(value || "").trim() ? `${label} is required` : undefined;
  return (
    <TextField
      required
      label={label}
      value={value}
      onChange={(_, v) => onChange(v || "")}
      onBlur={() => setTouched(true)}
      errorMessage={errorMessage}
    />
  );
}
